import React, { createRef, useEffect, useMemo, useRef } from "react";
import {
  Switch,
  Stack,
  styled,
  Snackbar,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  IconButton,
  Typography,
  Button,
  Paper,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import { useFormik } from "formik";
import axios from "axios";
import "./Form.css";
import DateTimePickerTextBox from "../../components/DateTimePickerTextBox";
import dayjs from "dayjs";
import Footer from "../../components/Footer";
import Textbox from "../../components/Textbox";
import Modal from "../../components/Modal";
import DisplayOrder from "../Customer/DisplayOrder";
import PrimaryLoader from "../../components/Loader/PrimaryLoader";
import { getFurnitureUrl, postPackingFormUrl } from "../../utils/Utils";
import LocationForm from "./LocationForm";
import * as Yup from "yup";
import { distanceAmountCalculation } from "../../utils/Utils";
const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 22,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(16px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 1,
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#1976d2",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgba(0, 35, 11, 0.2)",
    width: 24,
    height: 24,
    borderRadius: 12,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 13,
    opacity: 1,
    backgroundColor: theme.palette.mode === "dark" ? "#39393D" : "#E9E9EA",
    boxSizing: "border-box",
  },
}));

function PackersAndMoversForm() {
  const initialState = [
    {
      _id: "",
      room: "",
      furniture: [],
    },
  ];

  const [pickupLocationValue, setPickupLocationValue] = useState("");
  const [dropLocationValue, setDropLocationValue] = useState("");
  const [furnitureData, setFurnitureData] = useState(initialState);
  const [filteredFurnitureData, setFilteredFurnitureData] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
  });
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [previewFurnitureData, setPreviewFurnitureData] = useState([]);
  const [previewTotalCost, setPreviewTotalCost] = useState(0);
  const [openModal, setOpenModal] = useState(false);

  const [previewPickupLocationData, setPreviewPickupLocationData] = useState({
    pickupLocation: "",
    pickupFloor: "",
  });
  const [previewDropLocationData, setPreviewDropLocationData] = useState({
    dropLocation: "",
    dropFloor: "",
  });
  const [previewAddOns, setPreviewAddOns] = useState({
    singleLayerPacking: "",
    multiLayerPacking: "",
    unpacking: "",
    dismantling: "",
  });
  const [selectedHousingType, setSelectedHousingType] = useState("");
  const [loading, setLoading] = useState(false);
  const [distanceConfig, setDistanceConfig] = useState({
    _id: "",
    minDistance: 0,
    minCharge: 0,
    extraChargePerKM: 0,
  });

  const [isSubmitForm, setIsSubmitForm] = useState(false);
  const [distanceValue, setDistanceValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedPanels, setExpandedPanels] = useState(new Set());
  const [manuallyExpandedPanels, setManuallyExpandedPanels] = useState(new Set());
  const roomRefs = useRef({});

  const memoizedPreviewFurnitureData = useMemo(
    () => previewFurnitureData,
    [previewFurnitureData]
  );

  const filteredFurnitureDataWithSearch = useMemo(() => {
    if (!searchQuery.trim()) {
      setExpandedPanels(new Set());
      return furnitureData;
    }

    const query = searchQuery.toLowerCase();
    const newExpandedPanels = new Set();

    const filtered = furnitureData.map(room => ({
      ...room,
      furniture: room.furniture.map(category => {
        const matchedSubtypes = category.subtypes.filter(subtype =>
          subtype.name.toLowerCase().includes(query)
        );

        if (matchedSubtypes.length > 0) {
          newExpandedPanels.add(`${room.room}-${category.type}`);
        }

        return {
          ...category,
          subtypes: matchedSubtypes
        };
      }).filter(category => category.subtypes.length > 0)
    })).filter(room => room.furniture.length > 0);

    setExpandedPanels(newExpandedPanels);
    return filtered;
  }, [furnitureData, searchQuery]);

  const housingTypeOptions = [
    { value: "1BHK", label: "1BHK" },
    { value: "2BHK", label: "2BHK" },
    { value: "3BHK", label: "3BHK" },
    { value: "Other", label: "Other" },
  ];

  useEffect(() => {
    getFurnitureData();
  }, []);

  useEffect(() => {
    if (memoizedPreviewFurnitureData.length > 0) {
      setOpenPreviewModal(true);
    }
  }, [memoizedPreviewFurnitureData]);

  const getFurnitureData = async () => {
    setLoading(true);
    axios
      .get(getFurnitureUrl)
      .then((response) => {
        if (
          response.data.status &&
          response.data.message === "success" &&
          response.data.resultCode === 200
        ) {
          setFurnitureData(response.data.data.rooms);
          setDistanceConfig(response.data.data.distanceConfig);
          // Populate refs dynamically after data fetch
          const refs = response.data.data.rooms.reduce((acc, room) => {
            acc[room.room] = createRef();
            return acc;
          }, {});
          roomRefs.current = refs; // Update the current property of the ref
        }

        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setSnackbar({
          open: true,
          message: "Something went wrong",
        });
      });
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      mobile: "",
      email: "",
      withinCity: false,
      serviceLiftAtPickup: false,
      serviceLiftAtDrop: false,
      singleLayer: false,
      multiLayer: false,
      unpacking: false,
      dismantling: false,
      pickupLocation: null,
      dropLocation: null,
      pickupFloor: "",
      dropFloor: "",
      additionalItems: "",
      pickupDate: new Date(),
      distance: "",
      loadType: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      mobile: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
        .required("Mobile number is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      // loadType: Yup.string().required("Load type is required"),
      // pickupLocation: Yup.object({
      //   value: Yup.string().required("Pickup location is required"),
      // }).required("Pickup location is required"),
      // dropLocation: Yup.object({
      //   value: Yup.string().required("Drop location is required"),
      // }).required("Drop location is required"),
      pickupFloor: Yup.string().required("Pickup floor is required"),
      dropFloor: Yup.string().required("Drop floor is required"),
      pickupDate: Yup.string().required("Name is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!pickupLocationValue) {
        setSnackbar({
          open: true,
          message: "Please enter the origin location.",
        });
      } else if (!dropLocationValue) {
        setSnackbar({
          open: true,
          message: "Please enter the destination location.",
        });
      } else {
        if (filteredFurnitureData.length === 0) {
          setSnackbar({
            open: true,
            message: "Please select furniture detail",
          });
        } else {
          setLoading(true);
          let totalCost = 0;
          const tempData = filteredFurnitureData
            .filter((item) => item.quantity > 0)
            .map((furniture) => {
              totalCost += furniture.quantity * furniture.price;
              return {
                ...furniture,
                totalPrice: furniture.quantity * furniture.price,
              };
            });
          if (formik.values.singleLayer) {
            totalCost += 799;
          }
          if (formik.values.multiLayer) {
            totalCost += 1599;
          }
          if (formik.values.unpacking) {
            totalCost += 799;
          }
          if (formik.values.dismantling) {
            totalCost += 799;
          }
          const distance = parseInt(sessionStorage.getItem("calculatedDistance").trim());
          const distanceCost = distanceAmountCalculation(distance,distanceConfig);
          totalCost += distanceCost;
          let params = {
            name: values.name,
            email: values.email,
            mobile: values.mobile,
            serviceLiftAtPickup: values.serviceLiftAtPickup,
            serviceLiftAtDrop: values.serviceLiftAtDrop,
            houseType: selectedHousingType,
            loadType: values.loadType && values.loadType.trim() !== "" ? values.loadType : "",
            pickupDate: dayjs(values.pickupDate).format("MMMM D, YYYY h:mm A"),
            pickupLocationDetails: {
              pickupLocation: pickupLocationValue ?? "",
              pickupFloor: values.pickupFloor,
            },
            dropLocationDetails: {
              dropLocation: dropLocationValue ?? "",
              dropFloor: values.dropFloor,
            },
            addOns: {
              singleLayerPacking: values.singleLayer,
              multiLayerPacking: values.multiLayer,
              unpacking: values.unpacking,
              dismantling: values.dismantling,
            },
            furnitures: filteredFurnitureData,
            distance: distance,
            distanceConfig: distanceConfig,
            totalCost: totalCost,
            previousCost : totalCost,
          };
          let res = await axios.post(postPackingFormUrl, params);
          if (res.data.status && res.data.resultCode === 200) {
            setSnackbar({
              open: true,
              message: "Submitted successfully!",
            });
            setIsSubmitForm(true);
            setFilteredFurnitureData([]);
            await getFurnitureData();
            sessionStorage.setItem("calculatedDistance", "");
            setLoading(false);
          } else {
            setLoading(false);
            setSnackbar({
              open: true,
              message: "Something went wrong please try again!",
            });
          }
          resetForm();
         
        }
      }
    },
  });

  function filterFurniture(data) {
    const result = [];
    data.forEach((room) => {
      room.furniture.forEach((furnitureType) => {
        furnitureType.subtypes.forEach((furniture) => {
          if (furniture.quantity > 0) {
            result.push({
              name: furniture.name,
              quantity: furniture.quantity,
              price: furniture.price,
            });
          }
        });
      });
    });
    return result;
  }

  const updateQuantity = (roomName, furnitureType, subtypeId, newQuantity) => {
    // Update the furnitureData by directly modifying the necessary properties
    const updatedData = furnitureData.map((room) => {
      if (room.room === roomName) {
        // Find the room and update the furniture
        const updatedFurniture = room.furniture.map((category) => {
          if (category.type === furnitureType) {
            // Find the subtype to update
            const updatedSubtypes = category.subtypes.map((subtype) =>
              subtype._id === subtypeId
                ? { ...subtype, quantity: newQuantity }
                : subtype
            );
            return { ...category, subtypes: updatedSubtypes };
          }
          return category;
        });
        return { ...room, furniture: updatedFurniture };
      }
      return room;
    });

    // Optionally filter the updated data and set state
    const filteredData = filterFurniture(updatedData);
    if (filteredData.length > 0) {
      setFilteredFurnitureData(filteredData);
    } else {
      setFilteredFurnitureData([]);
    }
    setFurnitureData(updatedData);
  };

  const scrollToRoom = (roomName) => {
    roomRefs.current[roomName].current.scrollIntoView({ behavior: "smooth" });
  };

  const handleSwitchChange = (event) => {
    // singleLayerPacking: values.singleLayer,
    //             multiLayerPacking: values.multiLayer,
    if (event.target.name === "singleLayer" && event.target.checked) {
      formik.setFieldValue("multiLayer", false);
    }
    if (event.target.name === "multiLayer" && event.target.checked) {
      formik.setFieldValue("singleLayer", false);
    }
    formik.setFieldValue(event.target.name, event.target.checked);
  };

  const pickupDateChange = (date) => {
    formik.setFieldValue("pickupDate", date);
  };

  const handlePreview = () => {
    let totalCost = 0;
    if (pickupLocationValue !== "" && dropLocationValue !== "") {
      setDistanceValue(
        parseInt(sessionStorage.getItem("calculatedDistance").trim()) ?? 0
      );
    } else {
      setDistanceValue(0);
      sessionStorage.setItem("calculatedDistance", "");
    }
    const tempData = filteredFurnitureData
      .filter((item) => item.quantity > 0)
      .map((furniture) => {
        totalCost += furniture.quantity * furniture.price;
        return {
          ...furniture,
          totalPrice: furniture.quantity * furniture.price,
        };
      });
    setPreviewFurnitureData(tempData);

    // Add packaging costs (corrected field names)
    if (formik.values.singleLayer) {
      totalCost += 799;
    }
    if (formik.values.multiLayer) {
      totalCost += 1599;
    }
    if (formik.values.unpacking) {
      totalCost += 799;
    }
    if (formik.values.dismantling) {
      totalCost += 799;
    }
    setPreviewPickupLocationData({
      pickupLocation: pickupLocationValue ?? "",
      pickupFloor: formik.values.pickupFloor,
    });
    setPreviewDropLocationData({
      dropLocation: dropLocationValue ?? "",
      dropFloor: formik.values.dropFloor,
    });
    setPreviewTotalCost(totalCost);
    setPreviewAddOns({
      singleLayerPacking: formik.values.singleLayer,
      dismantling: formik.values.dismantling,
      multiLayerPacking: formik.values.multiLayer,
      unpacking: formik.values.unpacking,
    });
  };

  const handlePreviewClose = () => {
    setOpenPreviewModal(false);
  };

  const handleAccordionChange = (panelId) => (event, isExpanded) => {
    if (!searchQuery.trim()) {
      // Only allow manual control when not searching
      const newManuallyExpanded = new Set(manuallyExpandedPanels);
      if (isExpanded) {
        newManuallyExpanded.add(panelId);
      } else {
        newManuallyExpanded.delete(panelId);
      }
      setManuallyExpandedPanels(newManuallyExpanded);
    }
    // When searching, don't allow manual control
  };
  const handleHousingTypeChange = (selectedOption) => {
    setSelectedHousingType(selectedOption);
  };

  return (
    <>
      <Grid container>
        <Grid item xs={0} md={0.3}></Grid>
        <Grid item xs={0} md={6.0}>
          <Paper
            elevation={3}
            sx={{ padding: 3, marginTop: 3, backgroundColor: "#fff" }}
          >
            <form onSubmit={formik.handleSubmit} className="details">
              <Typography
                variant="h5"
                style={{ color: "#1976d2", textAlign: "center" }}
              >
                Natural Packers and Movers
              </Typography>
              <Typography
                style={{ color: "#ed823a", textAlign: "center" }}
                gutterBottom
              >
                Shifting hassles? Natural Packers and Movers has you covered!
                Move with ease starting at just Rs.1500.
              </Typography>
              <Grid
                sx={{
                  padding: "1rem",
                  border: "1px solid #1976d2",
                  borderRadius: "10px",
                  marginBottom: "1rem",
                }}
              >
                <Typography
                  variant="h5"
                  style={{ color: "#1976d2" }}
                  gutterBottom
                >
                  Personal Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Textbox
                      fullWidth
                      id="name"
                      name="name"
                      label="Name"
                      formik={formik}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Textbox
                      fullWidth
                      id="mobile"
                      name="mobile"
                      label="Mobile"
                      formik={formik}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Textbox
                      fullWidth
                      id="email"
                      name="email"
                      label="Email"
                      formik={formik}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                sx={{
                  padding: "1rem",
                  border: "1px solid #1976d2",
                  borderRadius: "10px",
                }}
              >
                <Typography
                  variant="h5"
                  style={{ color: "#1976d2" }}
                  gutterBottom
                >
                  Moving Details
                </Typography>

                {/* <div className="WithIn_city">
                                <Typography variant="h6">Within City</Typography>
                                <CustomSwitch
                                    name="withinCity"
                                    checked={formik.values.withinCity}
                                    onChange={handleSwitchChange}
                                    inputProps={{ "aria-label": "ant design" }}
                                />
                            </div> */}
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      height: "350px",
                      display: {
                        xs: "block",
                        md: "none",
                      },
                    }}
                  >
                    <LocationForm
                      setPickupLocationValue={setPickupLocationValue}
                      setDropLocationValue={setDropLocationValue}
                      isSubmitForm={isSubmitForm}
                      zoomLevel={10}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <Typography variant="h6" gutterBottom>
                        Load Type
                      </Typography>
                      <RadioGroup
                        row
                        name="loadType"
                        value={formik.values.loadType}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      >
                        <FormControlLabel
                          value="Part Load"
                          control={<Radio />}
                          label="Part Load"
                        />
                        <FormControlLabel
                          value="Full Load"
                          control={<Radio />}
                          label="Full Load"
                        />
                        <FormControlLabel
                          value="Inter City"
                          control={<Radio />}
                          label="Inter City"
                        />
                      </RadioGroup>
                    </FormControl>

                  </Grid>
                  {formik.values.loadType === "Part Load" && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          width: "100%",
                          backgroundColor: "#f5f5f5",
                          borderRadius: 2,
                          border: "1px solid #1976d2",
                          boxSizing: "border-box",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            width: "100%",
                            maxWidth: "100%",      // 🔥 important
                            display: "block",
                            textAlign: "justify",  // optional but looks great
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                          gutterBottom
                        >
                            ** In Part Load, உங்கள் பொருட்கள் சிறிய லாரிகளில் அல்லது பிற ஆர்டர்களுடன்
                            பகிர்ந்து ஏற்றப்படும், இதனால் நீங்கள் போக்குவரத்து கட்டணத்தில் 40% வரை
                            சேமிக்கலாம். இது குறைந்த அளவிலான பொருட்களுக்கு மட்டுமே பொருந்தும் **
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            width: "100%",
                            maxWidth: "100%",
                            display: "block",
                            textAlign: "justify",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                            ** In Part Load, your furniture will be transported using mini trucks or
                            shared with other orders, giving you up to 40% savings on transportation
                            charges. Applicable for low quantity items **
                        </Typography>
                      </Box>
                    </Grid>
                  )}


                  <Grid item xs={12} sm={6}>
                    <Grid
                      item
                      xs={12}
                      md={12}
                      display="flex"
                      gap={2}
                      sx={{ marginTop: "1rem" }}
                    >
                      <CustomSwitch
                        name="serviceLiftAtPickup"
                        checked={formik.values.serviceLiftAtPickup}
                        onChange={handleSwitchChange}
                        inputProps={{ "aria-label": "ant design" }}
                      />
                      <label style={{ marginRight: 16 }}>
                        Service lift available at pickup
                      </label>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Grid
                      item
                      xs={12}
                      md={12}
                      display="flex"
                      gap={2}
                      sx={{ marginTop: "1rem" }}
                    >
                      <CustomSwitch
                        name="serviceLiftAtDrop"
                        checked={formik.values.serviceLiftAtDrop}
                        onChange={handleSwitchChange}
                        inputProps={{ "aria-label": "ant design" }}
                      />
                      <label style={{ marginRight: 16 }}>
                        Service lift available at drop
                      </label>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Textbox
                      fullWidth
                      id="pickupFloor"
                      name="pickupFloor"
                      label="Pickup Floor"
                      formik={formik}
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Textbox
                      fullWidth
                      id="dropFloor"
                      name="dropFloor"
                      label="Drop Floor"
                      formik={formik}
                      type="number"
                    />
                  </Grid>
                  <Grid xs={12}>
                    {housingTypeOptions.map((option, index) => {
                      return (
                        <Button
                          key={index}
                          variant="contained"
                          onClick={() => handleHousingTypeChange(option.value)}
                          sx={{
                            backgroundColor:
                              selectedHousingType === option.value
                                ? "rgb(237, 130, 58)"
                                : undefined,
                            margin: "1rem 1rem 0rem 1rem",
                          }}
                        >
                          {option.label}
                        </Button>
                      );
                    })}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DateTimePickerTextBox
                      placeholder="From date"
                      isDatePicker
                      inputValue={dayjs(formik.values.pickupDate).format(
                        "MMMM D, YYYY h:mm A"
                      )}
                      dateValue={formik.values.pickupDate}
                      handleChange={pickupDateChange}
                      minimumDate={new Date()}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                sx={{
                  padding: "1rem",
                  border: "1px solid #1976d2",
                  borderRadius: "10px",
                  marginTop: "1rem",
                  marginBottom: "1rem",
                }}
              >
                <Grid container spacing={2} sx={{ marginBottom: "1rem" }}>
                  {filteredFurnitureDataWithSearch.length > 0 &&
                    filteredFurnitureDataWithSearch.map((room) => (
                      <Grid item key={room.room}>
                        <Button
                          variant="contained"
                          onClick={() => scrollToRoom(room.room)}
                        >
                          {room.room}
                        </Button>
                      </Grid>
                    ))}
                </Grid>
                <Grid
                  sx={{
                    padding: "1rem",
                    border: "1px solid #1976d2",
                    borderRadius: "10px",
                    marginTop: "1rem",
                    marginBottom: "1rem",
                  }}
                >
                  <Typography
                    style={{ color: "#1976d2" }}
                    variant="h5"
                    gutterBottom
                  >
                    Add Items
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ marginBottom: "1rem" }}
                  />
                  {filteredFurnitureDataWithSearch.length > 0 &&
                    filteredFurnitureDataWithSearch.map((room) => (
                      <div key={room.room} ref={roomRefs.current[room.room]}>
                        <Typography variant="h6" gutterBottom>
                          {room.room}
                        </Typography>
                        {room.furniture.map((category) => {
                          const panelId = `${room.room}-${category.type}`;
                          const isExpanded = searchQuery.trim() 
                            ? expandedPanels.has(panelId) // Auto-expand when searching
                            : manuallyExpandedPanels.has(panelId); // Manual control when not searching
                          const query = searchQuery.toLowerCase();
                          
                          return (
                          <Accordion
                            key={category.type}
                              expanded={isExpanded}
                              onChange={handleAccordionChange(panelId)}
                            sx={{ marginBottom: 2 }}
                          >
                            <AccordionSummary
                              expandIcon={<ArrowDropDownIcon />}
                              aria-controls="panel1a-content"
                              id="panel1a-header"
                            >
                              <Typography>{category.type}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {category.subtypes.map((subtype) => {
                                  const isHighlighted = searchQuery.trim() && 
                                    subtype.name.toLowerCase().includes(query);
                                  
                                  return (
                                    <div 
                                      key={subtype.id} 
                                      className="furn_div"
                                      style={{
                                        backgroundColor: isHighlighted ? '#e3f2fd' : 'transparent',
                                        padding: isHighlighted ? '8px' : '0',
                                        borderRadius: isHighlighted ? '4px' : '0',
                                        border: isHighlighted ? '1px solid #1976d2' : 'none',
                                        marginBottom: '4px'
                                      }}
                                    >
                                  <Grid
                                    container
                                    spacing={2}
                                    alignItems="center"
                                  >
                                    <Grid item xs={6}>
                                          <Typography 
                                            style={{
                                              fontWeight: isHighlighted ? 'bold' : 'normal',
                                              color: isHighlighted ? '#1976d2' : 'inherit'
                                            }}
                                          >
                                            {subtype.name}
                                          </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                      >
                                        <IconButton
                                          onClick={() =>
                                            updateQuantity(
                                              room.room,
                                              category.type,
                                              subtype._id,
                                              subtype.quantity - 1
                                            )
                                          }
                                          disabled={subtype.quantity <= 0}
                                          sx={{ color: "#d32f2f" }}
                                        >
                                          <RemoveIcon />
                                        </IconButton>
                                        <Typography>
                                          {subtype.quantity}
                                        </Typography>
                                        <IconButton
                                          onClick={() =>
                                            updateQuantity(
                                              room.room,
                                              category.type,
                                              subtype._id,
                                              subtype.quantity + 1
                                            )
                                          }
                                          sx={{ color: "#1976d2" }}
                                        >
                                          <AddIcon />
                                        </IconButton>
                                      </Stack>
                                    </Grid>
                                  </Grid>
                                </div>
                                  );
                                })}
                            </AccordionDetails>
                          </Accordion>
                          );
                        })}
                      </div>
                    ))}
                </Grid>
                <Grid xs={12}>
                  <Textbox
                    fullWidth
                    id="additionalItems"
                    name="additionalItems"
                    label="Enter additional items that are not listed above..."
                    formik={formik}
                  />
                </Grid>
              </Grid>
              <Grid
                sx={{
                  padding: "1rem",
                  border: "1px solid #1976d2",
                  borderRadius: "10px",
                  marginTop: "1rem",
                  marginBottom: "1rem",
                }}
              >
                <Typography
                  variant="h5"
                  style={{ color: "#1976d2" }}
                  gutterBottom
                >
                  Recommended add ons for you
                </Typography>
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    display="flex"
                    gap={2}
                    sx={{ marginBottom: "1rem" }}
                  >
                    <CustomSwitch
                      name="singleLayer"
                      checked={formik.values.singleLayer}
                      onChange={handleSwitchChange}
                      inputProps={{ "aria-label": "ant design" }}
                    />
                    <label style={{ marginRight: 16 }}>
                      Single Layer Packaging ₹799
                    </label>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    display="flex"
                    gap={2}
                    sx={{ marginBottom: "1rem" }}
                  >
                    <CustomSwitch
                      name="multiLayer"
                      checked={formik.values.multiLayer}
                      onChange={handleSwitchChange}
                      inputProps={{ "aria-label": "ant design" }}
                    />
                    <label style={{ marginRight: 16 }}>
                      Multi Layer Packaging ₹1,599
                    </label>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    display="flex"
                    gap={2}
                    sx={{ marginBottom: "1rem" }}
                  >
                    <CustomSwitch
                      name="unpacking"
                      checked={formik.values.unpacking}
                      onChange={handleSwitchChange}
                      inputProps={{ "aria-label": "ant design" }}
                    />
                    <label style={{ marginRight: 16 }}>Unpacking ₹799</label>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    display="flex"
                    gap={2}
                    sx={{ marginBottom: "1rem" }}
                  >
                    <CustomSwitch
                      name="dismantling"
                      checked={formik.values.dismantling}
                      onChange={handleSwitchChange}
                      inputProps={{ "aria-label": "ant design" }}
                    />
                    <label style={{ marginRight: 16 }}>Dismantling ₹799</label>
                  </Grid>
                  {/* <Grid container>
                    <Typography
                      style={{ color: "#ff3333", fontSize: "12px" }}
                      gutterBottom
                    >
                      * Please note that transport charges ₹
                      {distanceConfig.minCharge} upto{" "}
                      {distanceConfig.minDistance} KM, beyond{" "}
                      {distanceConfig.minDistance} KM ₹
                      {distanceConfig.extraChargePerKM} per km charges
                      applicable
                    </Typography>
                  </Grid> */}
                </Grid>
              </Grid>
              <Grid container display="flex" justifyContent="flex-end">
                <Grid xs={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handlePreview}
                    disabled={filteredFurnitureData.length === 0 ? true : false}
                  >
                    Preview
                  </Button>
                </Grid>
                <Grid xs={3} ml={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
        <Grid item xs={0} md={0.1}></Grid>
        <Box
          sx={{
            position: "fixed",
            top: { xs: 0, md: 22 },
            left: { xs: 0, md: "auto" },
            right: { xs: 0, md: openModal ? 0 : 15 },
            bottom: { xs: 0, md: "auto" },
            width: { xs: "100%", md: "45%" },
            height: { xs: "100dvh", md: "93%" },
            display: {
              xs: openModal ? "flex" : "none", // flex only on mobile if open
              md: "block",
            },
            justifyContent: { xs: "center", md: "unset" },
            alignItems: { xs: "center", md: "unset" },
            backgroundColor: { xs: "rgba(0, 0, 0, 0.4)", md: "transparent" }, // dim background on mobile
            zIndex: 10,
            // padding: { xs: 0, md: 0 },
            transition: "all 0.3s ease",
          }}
        >
          <Box
            sx={{
              width: { xs: "90%", md: "100%" },
              height: { xs: "70dvh", md: "93%" }, // 65% height on mobile
              backgroundColor: "#fff",
              borderRadius: {
                xs: 2,
                md: openModal ? "4px" : 0,
              },
              boxShadow: 3,
              overflowY: "auto",
              // padding: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <LocationForm
              setPickupLocationValue={setPickupLocationValue}
              setDropLocationValue={setDropLocationValue}
              isSubmitForm={isSubmitForm}
              zoomLevel={15}
            />
          </Box>
        </Box>

        {/* <Fab
          color="red"
          onClick={() => setOpenModal((prev) => !prev)}
          sx={{
            display: {
              xs: "flex",
              md: "none",
            },
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <LocationOnIcon />
        </Fab> */}
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          open={snackbar.open}
          onClose={() => {
            setSnackbar({ ...snackbar, open: false });
          }}
          message={snackbar.message}
        />
        {openPreviewModal && (
          <Modal open={openPreviewModal} setOpen={handlePreviewClose}>
            <DisplayOrder
              name={formik.values.name}
              email={formik.values.email}
              mobile={formik.values.mobile}
              serviceLiftAtPickup={formik.values.serviceLiftAtPickup}
              serviceLiftAtDrop={formik.values.serviceLiftAtDrop}
              pickupDate={formik.values.pickupDate.toString()}
              pickupLocationDetails={previewPickupLocationData}
              dropLocationDetails={previewDropLocationData}
              furnitures={filteredFurnitureData}
              showPrice={false}
              totalCost={previewTotalCost}
              addOns={previewAddOns}
              housingType={selectedHousingType}
              loadType={formik.values.loadType}
              additionalItems={formik.values.additionalItems}
              distanceConfig={distanceConfig}
              distance={distanceValue}
            />
            <Grid container display="flex" justifyContent="flex-end" mt={2}>
              <Grid xs={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handlePreviewClose}
                >
                  Close
                </Button>
              </Grid>
            </Grid>
          </Modal>
        )}
      </Grid>
      <Footer />
      <PrimaryLoader openModal={loading} />
    </>
  );
}

export default PackersAndMoversForm;
